import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Zap, Send, Users, Search, Filter, CheckCircle, Clock, Eye,
  LogIn, Camera, Sparkles, Calendar, Heart, Droplet, RefreshCw,
  ChevronRight, User, AlertTriangle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { api } from '../utils/api';

const NudgeCenter = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [nudges, setNudges] = useState([]);
  const [nudgeTemplates, setNudgeTemplates] = useState([]);
  const [patients, setPatients] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [selectedNudgeType, setSelectedNudgeType] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('send');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [templatesRes, nudgesRes, patientsRes, statsRes] = await Promise.all([
        api.getNudgeTemplates(),
        api.getNudges(),
        api.getPatients(),
        api.getNudgeStats(),
      ]);
      setNudgeTemplates(templatesRes.data);
      setNudges(nudgesRes.data);
      setPatients(patientsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load nudge center');
    } finally {
      setLoading(false);
    }
  };

  const getNudgeIcon = (type) => {
    const icons = {
      login_reminder: <LogIn className="w-5 h-5" />,
      facial_scan: <Camera className="w-5 h-5" />,
      coach360_update: <Sparkles className="w-5 h-5" />,
      weekly_review: <Calendar className="w-5 h-5" />,
      mood_checkin: <Heart className="w-5 h-5" />,
      hydration_reminder: <Droplet className="w-5 h-5" />,
    };
    return icons[type] || <Zap className="w-5 h-5" />;
  };

  const getNudgeColor = (type) => {
    const colors = {
      login_reminder: 'bg-blue-100 text-blue-700 border-blue-200',
      facial_scan: 'bg-purple-100 text-purple-700 border-purple-200',
      coach360_update: 'bg-pink-100 text-pink-700 border-pink-200',
      weekly_review: 'bg-green-100 text-green-700 border-green-200',
      mood_checkin: 'bg-red-100 text-red-700 border-red-200',
      hydration_reminder: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    };
    return colors[type] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getStatusBadge = (status) => {
    const badges = {
      sent: <Badge className="bg-yellow-100 text-yellow-700">Sent</Badge>,
      delivered: <Badge className="bg-blue-100 text-blue-700">Delivered</Badge>,
      read: <Badge className="bg-green-100 text-green-700">Read</Badge>,
      failed: <Badge className="bg-red-100 text-red-700">Failed</Badge>,
    };
    return badges[status] || <Badge>Unknown</Badge>;
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleSelectPatient = (patientId) => {
    setSelectedPatients(prev => 
      prev.includes(patientId) 
        ? prev.filter(id => id !== patientId)
        : [...prev, patientId]
    );
  };

  const handleSelectAll = () => {
    if (selectedPatients.length === filteredPatients.length) {
      setSelectedPatients([]);
    } else {
      setSelectedPatients(filteredPatients.map(p => p.id));
    }
  };

  const handleSendNudges = async () => {
    if (selectedPatients.length === 0 || !selectedNudgeType) {
      toast.error('Please select patients and a nudge type');
      return;
    }

    try {
      setSending(true);
      
      if (selectedPatients.length === 1) {
        // Single nudge
        await api.sendNudge({
          patient_id: selectedPatients[0],
          nudge_type: selectedNudgeType,
          message: customMessage || undefined,
        });
      } else {
        // Batch nudges
        await api.sendBatchNudges(selectedPatients, selectedNudgeType, customMessage || undefined);
      }
      
      toast.success(`Nudge${selectedPatients.length > 1 ? 's' : ''} sent to ${selectedPatients.length} member${selectedPatients.length > 1 ? 's' : ''}!`);
      setSelectedPatients([]);
      setSelectedNudgeType('');
      setCustomMessage('');
      await loadData();
    } catch (error) {
      toast.error('Failed to send nudges');
    } finally {
      setSending(false);
    }
  };

  const filteredPatients = patients.filter(p => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return p.first_name.toLowerCase().includes(query) ||
           p.last_name.toLowerCase().includes(query) ||
           p.email.toLowerCase().includes(query);
  });

  const selectedTemplate = nudgeTemplates.find(t => t.nudge_type === selectedNudgeType);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-64"></div>
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="skeleton h-24"></div>)}
        </div>
        <div className="skeleton h-96"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="nudge-center">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Nudge Center
          </h1>
          <p className="text-gray-600 mt-1">Send engagement prompts to your members</p>
        </div>
        <Button
          onClick={loadData}
          variant="outline"
          className="rounded-full"
          data-testid="refresh-nudges-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Sent</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Send className="w-8 h-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Delivered</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.delivered}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Read</p>
                  <p className="text-2xl font-bold text-green-600">{stats.read}</p>
                </div>
                <Eye className="w-8 h-8 text-green-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.sent}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="send" className="flex items-center space-x-2" data-testid="send-tab">
            <Send className="w-4 h-4" />
            <span>Send Nudges</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center space-x-2" data-testid="history-tab">
            <Clock className="w-4 h-4" />
            <span>History ({nudges.length})</span>
          </TabsTrigger>
        </TabsList>

        {/* Send Nudges Tab */}
        <TabsContent value="send">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Nudge Type Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Zap className="w-5 h-5" style={{ color: '#8F2FDF' }} />
                  <span>Select Nudge Type</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {nudgeTemplates.map((template) => (
                    <div
                      key={template.id}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedNudgeType === template.nudge_type
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedNudgeType(template.nudge_type)}
                      data-testid={`nudge-type-${template.nudge_type}`}
                    >
                      <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full mb-2 ${getNudgeColor(template.nudge_type)}`}>
                        {getNudgeIcon(template.nudge_type)}
                      </div>
                      <h4 className="font-semibold text-sm">{template.title}</h4>
                      <p className="text-xs text-gray-500 mt-1">{template.description}</p>
                    </div>
                  ))}
                </div>

                {selectedTemplate && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium mb-2">Default Message:</p>
                    <p className="text-sm text-gray-600 italic">&ldquo;{selectedTemplate.default_message}&rdquo;</p>
                    <div className="mt-3">
                      <label className="text-sm font-medium">Custom Message (optional)</label>
                      <Textarea
                        placeholder="Override the default message..."
                        value={customMessage}
                        onChange={(e) => setCustomMessage(e.target.value)}
                        className="mt-1"
                        rows={2}
                        data-testid="custom-message-input"
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Patient Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Users className="w-5 h-5" style={{ color: '#8F2FDF' }} />
                    <span>Select Members</span>
                  </div>
                  {selectedPatients.length > 0 && (
                    <Badge variant="secondary">{selectedPatients.length} selected</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <div className="relative flex-1">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        placeholder="Search members..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9"
                        data-testid="search-patients"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAll}
                      data-testid="select-all-btn"
                    >
                      {selectedPatients.length === filteredPatients.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>

                  <div className="max-h-[300px] overflow-auto space-y-2">
                    {filteredPatients.map((patient) => (
                      <div
                        key={patient.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedPatients.includes(patient.id)
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                        onClick={() => handleSelectPatient(patient.id)}
                        data-testid={`patient-select-${patient.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={selectedPatients.includes(patient.id)}
                              onChange={() => {}}
                              className="rounded border-gray-300"
                            />
                            <div>
                              <p className="font-medium text-sm">{patient.first_name} {patient.last_name}</p>
                              <p className="text-xs text-gray-500">{patient.email}</p>
                            </div>
                          </div>
                          <div className={`px-2 py-1 rounded text-xs font-medium ${
                            patient.risk_score <= 2 ? 'bg-red-100 text-red-700' :
                            patient.risk_score === 3 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            Risk {patient.risk_score}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Send Button */}
                <div className="mt-4 pt-4 border-t">
                  <Button
                    onClick={handleSendNudges}
                    disabled={selectedPatients.length === 0 || !selectedNudgeType || sending}
                    className="w-full rounded-full"
                    style={{ background: 'linear-gradient(135deg, #8F2FDF 0%, #CE08B1 100%)' }}
                    data-testid="send-nudges-btn"
                  >
                    {sending ? 'Sending...' : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Nudge{selectedPatients.length > 1 ? 's' : ''} to {selectedPatients.length} Member{selectedPatients.length > 1 ? 's' : ''}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Clock className="w-5 h-5" />
                <span>Nudge History</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {nudges.map((nudge) => (
                  <div
                    key={nudge.id}
                    className="p-4 rounded-xl border border-gray-200 hover:shadow-md transition-shadow"
                    data-testid={`nudge-history-${nudge.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full ${getNudgeColor(nudge.nudge_type)}`}>
                          {getNudgeIcon(nudge.nudge_type)}
                        </div>
                        <div>
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="font-semibold">{nudge.title}</h4>
                            {getStatusBadge(nudge.status)}
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{nudge.message}</p>
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span className="flex items-center">
                              <User className="w-3 h-3 mr-1" />
                              {nudge.patient_name}
                            </span>
                            <span className="flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              Sent {formatTime(nudge.created_at)}
                            </span>
                            {nudge.delivered_at && (
                              <span className="flex items-center text-blue-600">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Delivered {formatTime(nudge.delivered_at)}
                              </span>
                            )}
                            {nudge.read_at && (
                              <span className="flex items-center text-green-600">
                                <Eye className="w-3 h-3 mr-1" />
                                Read {formatTime(nudge.read_at)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/patients/${nudge.patient_id}`)}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}

                {nudges.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Zap className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No nudges sent yet</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NudgeCenter;
